import { AccountStatus } from "@components/AccountStatus"
import { PersonIdentity } from "@components/PersonIdentity"
import { Badge } from "@components/ui/Badge"
import { RowActions, type RowAction } from "@components/ui/RowActions"
import { Table } from "@components/ui/Table"
import { Mail, Pencil, RotateCcw, Trash2 } from "lucide-react"
import { memo } from "react"
import { useTranslation } from "react-i18next"

import type { AdminUserRow } from "../types"

import { ROLE } from "@/constants/roles"

interface Props {
  rows: AdminUserRow[]
  caption: string
  stale?: boolean
  sort?: { key: string; desc: boolean }
  onSort?: (key: string) => void
  canManage: boolean
  /** The signed-in account: no "Mover a papelera" on yourself (PRD-32 §FR-13). */
  currentAdminId?: string
  busyId: string | null
  onReinvite: (row: AdminUserRow) => void
  onEdit: (row: AdminUserRow) => void
  onTrash: (row: AdminUserRow) => void
  onRestore: (row: AdminUserRow) => void
}

// No Institution column (PRD-60 chunk B): the list is already the header's
// institution, so every row repeated the same name.
export const AdminUsersTable = memo(function AdminUsersTable({
  rows,
  caption,
  stale,
  sort,
  onSort,
  canManage,
  currentAdminId,
  busyId,
  onReinvite,
  onEdit,
  onTrash,
  onRestore,
}: Props) {
  const { t } = useTranslation(["admin-users", "common"])

  function actionsFor(row: AdminUserRow) {
    const name = row.full_name || row.email
    if (row.deleted_at) {
      return (
        <RowActions
          subject={name}
          primary={{
            label: t("actions.restore"),
            icon: <RotateCcw className="h-4 w-4" />,
            onClick: () => onRestore(row),
          }}
        />
      )
    }
    const edit: RowAction = {
      label: t("actions.edit"),
      icon: <Pencil className="h-4 w-4" />,
      onClick: () => onEdit(row),
    }
    const trash: RowAction[] =
      row.id === currentAdminId
        ? []
        : [
            {
              label: t("actions.trash"),
              icon: <Trash2 className="h-4 w-4" />,
              onClick: () => onTrash(row),
              danger: true,
            },
          ]
    if (row.invite_status === "accepted") {
      return <RowActions subject={name} primary={edit} items={trash} />
    }
    return (
      <RowActions
        subject={name}
        primary={{
          label: t("actions.reinvite"),
          icon: <Mail className="h-4 w-4" />,
          onClick: () => onReinvite(row),
          loading: busyId === row.id,
        }}
        items={[edit, ...trash]}
      />
    )
  }

  return (
    <Table
      caption={caption}
      rows={rows}
      rowKey={(r) => r.id}
      identity={{
        header: t("fields.user"),
        sortKey: "name",
        cell: (r) => (
          <PersonIdentity
            name={r.full_name || r.email}
            email={r.email}
            badge={
              r.id === currentAdminId ? <Badge variant="outline">{t("you")}</Badge> : undefined
            }
          />
        ),
      }}
      columns={[
        {
          key: "role",
          sortKey: "role",
          header: t("fields.role"),
          cell: (r) => (
            <Badge variant={r.role === ROLE.platformSuperuser ? "accent" : "neutral"}>
              {t(`common:account.role.${r.role}`)}
            </Badge>
          ),
        },
        {
          key: "status",
          sortKey: "status",
          header: t("fields.status"),
          cell: (r) => (
            <AccountStatus
              status={r.invite_status}
              invitedAt={r.invited_at}
              acceptedAt={r.invite_accepted_at}
              expiresAt={r.invite_expires_at}
              deletedAt={r.deleted_at}
            />
          ),
        },
      ]}
      actions={canManage ? actionsFor : undefined}
      actionsHeader={t("fields.actions")}
      stale={stale}
      cards="inline"
      sort={sort}
      onSort={onSort}
    />
  )
})
